import React, { useEffect, useState } from 'react';
import {
  createRestaurantPlatform,
  updateRestaurantPlatform,
  FrontendRestaurantSectionPlatform,
} from '../../services/restaurant/restaurantPlatformService';
import { Platform } from '../../types/Platforms';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import { canUseSuperAdminFeatures } from '../../services/accessManagement/accessManagement';
import {
  fetchSetupPlatformSettings,
  updateSetupPlatformSettings,
} from '../../services/admin/adminSetupService';

export type Mode = 'create' | 'edit';

/**
 * One boolean row of the super-admin "Platform settings" block: label, an ON/OFF pill that flips it,
 * and the description of what the current state means. Shared by every setting so the rows can never
 * drift visually.
 */
const PlatformSettingToggle: React.FC<{
  label: string;
  value: boolean;
  onChange: (next: boolean) => void;
  stateOnLabel: string;
  stateOffLabel: string;
  description: string;
}> = ({ label, value, onChange, stateOnLabel, stateOffLabel, description }) => (
  <div>
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={
          value
            ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/60 transition'
            : 'px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-600 transition'
        }
      >
        {value ? stateOnLabel : stateOffLabel}
      </button>
    </div>
    <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1.5">{description}</p>
  </div>
);

interface SavePlatformModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: Mode;
  restaurantSectionId: string;
  initial?: FrontendRestaurantSectionPlatform;
  onSaved: () => void;
}

export const SavePlatformModal: React.FC<SavePlatformModalProps> = ({
  isOpen,
  onClose,
  mode,
  restaurantSectionId,
  initial,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState(initial?.platformName || '');
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [orderEmails, setOrderEmails] = useState<string[]>(initial?.orderEmails || []);
  const [emailInput, setEmailInput] = useState('');
  const [orderClientNames, setOrderClientNames] = useState<string[]>(initial?.orderClientNames || []);
  const [clientNameInput, setClientNameInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * Super-admin-only "Platform settings" block (restaurant_platforms.settings). Only a super admin
   * may WRITE these, and only through the super-admin setup endpoints — this block is the whole UI.
   *
   * Raw `settings` does ride along on the client-facing /api/restaurantPlatforms payload (the routes
   * select `rp.*`), but the client maps exactly one key out of it: `combinedOrderingAccount`, exposed
   * as a derived read-only boolean because every client page's food/drinks split needs it. Every
   * other key — `vangelderPakbonMode` and any future ingestion-behaviour flag — is neither read nor
   * writable from the client side.
   *
   * Per-setting visibility: the block itself shows for any super admin editing a stored row; the
   * Van Gelder row is gated on the STORED platform name, not the dropdown value (the setting belongs
   * to the row as it exists server-side, so switching the dropdown doesn't conjure a setting for a
   * different supplier), while the combined-account row applies to every platform.
   *
   * Identity is `is_super_admin_identity`, NOT `is_super_admin`: this page is reached by impersonating
   * the client, and `backendUser` is then the impersonated admin (its /api/users/me call goes through
   * the impersonation interceptor). The identity flag still describes the real person behind the
   * session — matching `requireSuperAdminIdentity` on the endpoints behind this block.
   */
  const { backendUser } = useAuth();
  const isSuperAdmin = canUseSuperAdminFeatures(backendUser);
  const showsPlatformSettings = isSuperAdmin && mode === 'edit';
  const showsVangelderPakbonMode = initial?.platformName === Platform.VANGELDER;
  const [pakbonMode, setPakbonMode] = useState<boolean | null>(null);
  const [savedPakbonMode, setSavedPakbonMode] = useState<boolean | null>(null);
  // null until the settings fetch lands — the block renders nothing before that, so a toggle can
  // never show a guessed default.
  const [combinedAccount, setCombinedAccount] = useState<boolean | null>(null);
  const [savedCombinedAccount, setSavedCombinedAccount] = useState<boolean | null>(null);

  const platformOptions = (Object.values(Platform) as unknown[])
    .filter((v): v is Platform => typeof v === 'string');

  useEffect(() => {
    if (isOpen) {
      setPlatform(initial?.platformName || '');
      setEnabled(initial?.enabled ?? true);
      setOrderEmails(initial?.orderEmails || []);
      setEmailInput('');
      setOrderClientNames(initial?.orderClientNames || []);
      setClientNameInput('');
      setError(null);
      setSaving(false);
      setPakbonMode(null);
      setSavedPakbonMode(null);
      setCombinedAccount(null);
      setSavedCombinedAccount(null);
    }
  }, [isOpen, initial]);

  useEffect(() => {
    if (!isOpen || !showsPlatformSettings || !initial) return;

    let cancelled = false;
    fetchSetupPlatformSettings(initial.id)
      .then(settings => {
        if (cancelled) return;
        setPakbonMode(settings.vangelderPakbonMode);
        setSavedPakbonMode(settings.vangelderPakbonMode);
        setCombinedAccount(settings.combinedOrderingAccount);
        setSavedCombinedAccount(settings.combinedOrderingAccount);
      })
      .catch(err => {
        // Leaves the section unrendered rather than showing a wrong default — these flags decide
        // which Van Gelder document becomes the stored order, and how spend splits food vs drinks.
        console.error(err);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, showsPlatformSettings, initial]);

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (trimmed && !orderEmails.includes(trimmed)) {
      setOrderEmails([...orderEmails, trimmed]);
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setOrderEmails(orderEmails.filter(e => e !== email));
  };

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleAddClientName = () => {
    const trimmed = clientNameInput.trim();
    if (trimmed && !orderClientNames.includes(trimmed)) {
      setOrderClientNames([...orderClientNames, trimmed]);
      setClientNameInput('');
    }
  };

  const handleRemoveClientName = (name: string) => {
    setOrderClientNames(orderClientNames.filter(n => n !== name));
  };

  const handleClientNameInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddClientName();
    }
  };

  const handleSave = async () => {
    if (!platform) {
      setError(t('restaurantPlatforms.errorPlatform'));
      return;
    }
    // Include any leftover text in inputs that the user forgot to "Add"
    const trimmedEmailInput = emailInput.trim();
    const finalEmails = trimmedEmailInput && !orderEmails.includes(trimmedEmailInput)
      ? [...orderEmails, trimmedEmailInput]
      : orderEmails;

    const trimmedClientNameInput = clientNameInput.trim();
    const finalClientNames = trimmedClientNameInput && !orderClientNames.includes(trimmedClientNameInput)
      ? [...orderClientNames, trimmedClientNameInput]
      : orderClientNames;

    setSaving(true);
    setError(null);
    try {
      if (mode === 'create') {
        await createRestaurantPlatform({
          restaurantSectionId,
          platformName: platform,
          enabled,
          loginUsername: initial?.loginUsername || '',
          loginPassword: initial?.loginPassword || '',
          orderEmails: finalEmails.length > 0 ? finalEmails : undefined,
          orderClientNames: finalClientNames.length > 0 ? finalClientNames : undefined,
        });
      } else if (initial) {
        await updateRestaurantPlatform(initial.id, {
          platformName: platform,
          enabled,
          loginUsername: initial.loginUsername || '',
          loginPassword: initial.loginPassword || '',
          orderEmails: finalEmails.length > 0 ? finalEmails : undefined,
          orderClientNames: finalClientNames.length > 0 ? finalClientNames : undefined,
        });

        // Separate endpoint (super-admin, merges into the settings jsonb server-side), so only send
        // the toggles that actually moved — and only fire at all when at least one did.
        const settingsPatch: { vangelderPakbonMode?: boolean; combinedOrderingAccount?: boolean } = {};
        if (pakbonMode !== null && pakbonMode !== savedPakbonMode) {
          settingsPatch.vangelderPakbonMode = pakbonMode;
        }
        if (combinedAccount !== null && combinedAccount !== savedCombinedAccount) {
          settingsPatch.combinedOrderingAccount = combinedAccount;
        }
        if (Object.keys(settingsPatch).length > 0) {
          try {
            await updateSetupPlatformSettings(initial.id, settingsPatch);
            if (settingsPatch.vangelderPakbonMode !== undefined) setSavedPakbonMode(pakbonMode);
            if (settingsPatch.combinedOrderingAccount !== undefined) setSavedCombinedAccount(combinedAccount);
          } catch (err) {
            // The supplier row itself already saved — keep the modal open with the precise reason
            // instead of the generic "could not save supplier".
            console.error(err);
            setError(
              t('restaurantPlatforms.platformSettings.saveFailed', { message: (err as Error).message })
            );
            return;
          }
        }
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error(err);
      if (err?.message === 'DUPLICATE_PLATFORM') {
        setError(t('restaurantPlatforms.errorDuplicate'));
      } else {
        setError(t('restaurantPlatforms.errorSave'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] border border-zinc-200 dark:border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('restaurantPlatforms.addSupplier') : t('restaurantPlatforms.editSupplier')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-5">
            {/* Platform Selection */}
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantPlatforms.platform')}
              </label>
              <select
                id="platform"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                value={platform}
                onChange={e => setPlatform(e.target.value as Platform)}
              >
                <option value="" disabled>{t('restaurantPlatforms.selectPlatform')}</option>
                {platformOptions.map(opt => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Enabled Toggle */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
              <input
                type="checkbox"
                id="enabled"
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600 text-emerald-600 dark:text-textGreen-dark focus:ring-emerald-500 dark:focus:ring-emerald-600"
              />
              <div>
                <label htmlFor="enabled" className="text-sm font-medium text-zinc-900 dark:text-zinc-100 cursor-pointer">
                  {t('restaurantPlatforms.enabled')}
                </label>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">
                  {t('restaurantPlatforms.enabledDescription')}
                </p>
              </div>
            </div>

            {/* Order Emails */}
            <div>
              <label htmlFor="orderEmails" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantPlatforms.orderEmail')}
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="email"
                    id="orderEmails"
                    className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={handleEmailInputKeyDown}
                    placeholder="noreply@supplier.com"
                  />
                  <button
                    type="button"
                    onClick={handleAddEmail}
                    className="px-4 py-2.5 rounded-xl bg-emerald-600 dark:bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-700 text-white dark:text-white font-medium transition"
                  >
                    Add
                  </button>
                </div>
                {orderEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {orderEmails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="hover:text-emerald-900 dark:hover:text-emerald-100"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1.5">
                {t('restaurantPlatforms.orderEmailDescription')}
              </p>
            </div>

            {/* Client Names */}
            <div>
              <label htmlFor="orderClientNames" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantPlatforms.restaurantNameInEmail')}
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="orderClientNames"
                    className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                    value={clientNameInput}
                    onChange={e => setClientNameInput(e.target.value)}
                    onKeyDown={handleClientNameInputKeyDown}
                    placeholder="Exact restaurant name in email"
                  />
                  <button
                    type="button"
                    onClick={handleAddClientName}
                    className="px-4 py-2.5 rounded-xl bg-emerald-600 dark:bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-700 text-white dark:text-white font-medium transition"
                  >
                    Add
                  </button>
                </div>
                {orderClientNames.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {orderClientNames.map((name) => (
                      <span
                        key={name}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm"
                      >
                        {name}
                        <button
                          type="button"
                          onClick={() => handleRemoveClientName(name)}
                          className="hover:text-emerald-900 dark:hover:text-emerald-100"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1.5">
                {t('restaurantPlatforms.restaurantNameInEmailDescription')}
              </p>
            </div>

            {/* Platform settings — super-admin only, hidden for clients (see state comment above) */}
            {showsPlatformSettings && combinedAccount !== null && (
              <div className="p-4 rounded-xl bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/50 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    {t('restaurantPlatforms.platformSettings.title')}
                  </h3>
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
                    {t('restaurantPlatforms.platformSettings.superAdminOnly')}
                  </p>
                </div>
                {showsVangelderPakbonMode && pakbonMode !== null && (
                  <PlatformSettingToggle
                    label={t('adminSetup.vangelderPakbonMode.label')}
                    value={pakbonMode}
                    onChange={setPakbonMode}
                    stateOnLabel={t('adminSetup.vangelderPakbonMode.stateOn')}
                    stateOffLabel={t('adminSetup.vangelderPakbonMode.stateOff')}
                    description={
                      pakbonMode
                        ? t('adminSetup.vangelderPakbonMode.on')
                        : t('adminSetup.vangelderPakbonMode.off')
                    }
                  />
                )}
                <PlatformSettingToggle
                  label={t('adminSetup.combinedOrderingAccount.label')}
                  value={combinedAccount}
                  onChange={setCombinedAccount}
                  stateOnLabel={t('adminSetup.combinedOrderingAccount.stateOn')}
                  stateOffLabel={t('adminSetup.combinedOrderingAccount.stateOff')}
                  description={
                    combinedAccount
                      ? t('adminSetup.combinedOrderingAccount.on')
                      : t('adminSetup.combinedOrderingAccount.off')
                  }
                />
              </div>
            )}

            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
              >
                {t('restaurantPlatforms.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (mode === 'create' ? t('restaurantPlatforms.creating') : t('restaurantPlatforms.saving')) : (mode === 'create' ? t('restaurantPlatforms.create') : t('restaurantPlatforms.saveChanges'))}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};